import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RoutineSkillsEditor, { withKey, toRows, toPayloadSkills } from './RoutineSkillsEditor';
import RoutineSuggester from './RoutineSuggester';
import { useFeatures } from '../../hooks/useFeature';
import { APPARATUS, STATUS } from './routineConstants';
import { totalDifficulty } from '../../utils/routineDifficulty';
import './GymnastRoutines.css';

// Ways to start a new routine, shown as segments
const MODES = [
  { id: 'personal', title: 'New', hint: 'Pick the skills', label: 'New personal routine' },
  { id: 'link', title: 'Level routine', hint: 'Stays in sync', label: 'Link a level routine' },
  { id: 'set', title: 'Set routine', hint: 'Copy a competition set', label: 'Copy a competition set routine' },
  { id: 'suggest', title: 'Suggest', hint: 'Built from their skills', label: 'Suggest a routine from their skills' },
];

const levelRoutineLabel = (routine) => routine.name || `Routine ${routine.order}`;

// A level routine's rows, shaped like a gymnast routine's skills
const levelRoutineSkills = (routine) => (routine.routineSkills || []).map(rs => (rs.skill
  ? { ...rs.skill, id: rs.id, skillId: rs.skill.id, order: rs.order, isImplicit: false }
  : { id: rs.id, skillId: null, name: rs.customSkillName, order: rs.order, isImplicit: true, difficulty: rs.difficulty ?? null, dmtDifficulty: rs.difficulty ?? null, figNotation: rs.figNotation ?? null }));

// Create or edit a gymnast routine. `routine` is null when creating. onSave
// receives the API payload and rejects on failure, in which case the form is
// left as it was so nothing the coach typed is lost. Its second argument is
// how the routine will look (skills with names and DD), for showing it before
// it reaches the server when there's no signal. defaultApparatus sets the
// starting apparatus for a new routine. byGymnast is the gymnast entering
// their own routine: personal routines only, and the coach sets the status.
const GymnastRoutineEditor = ({ routine, gymnastId, onSave, onCancel, defaultApparatus = 'TRA', byGymnast = false, saveLabel = 'Save routine' }) => {
  const has = useFeatures();
  const isNew = !routine;
  const isLinked = Boolean(routine?.levelRoutineId);
  const [mode, setMode] = useState('personal'); // only switchable when creating
  const [apparatus, setApparatus] = useState(routine?.apparatus || defaultApparatus);
  const [name, setName] = useState(routine?.name || '');
  // A name filled in from a set or suggested routine, not typed: the next
  // one chosen may replace it. A typed name is kept.
  const [nameFilled, setNameFilled] = useState(false);
  const fillName = (n) => {
    if (name.trim() && !nameFilled) return;
    setName(n);
    setNameFilled(true);
  };
  const [status, setStatus] = useState(routine?.status || 'WORKING_ON');
  const [notes, setNotes] = useState(routine?.notes || '');
  const [rows, setRows] = useState(() => (routine && !isLinked ? toRows(routine.skills) : []));
  const [levelRoutineId, setLevelRoutineId] = useState('');
  const [levels, setLevels] = useState(null);
  const [setRoutines, setSetRoutines] = useState(null);
  const [setRoutineId, setSetRoutineId] = useState('');
  const [availableSkills, setAvailableSkills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const linking = isNew && mode === 'link';
  const editsSkills = !isLinked && !linking;
  // Where the routine comes from: only asked for a new routine, or shown for a linked one
  const showStart = (isNew && !byGymnast) || isLinked;

  useEffect(() => {
    if (!editsSkills) return;
    axios.get('/api/skills')
      .then(res => setAvailableSkills(res.data))
      .catch(() => setFormError('Could not load the skill library'));
  }, [editsSkills]);

  useEffect(() => {
    if (!linking || levels) return;
    axios.get('/api/levels')
      .then(res => setLevels(res.data))
      .catch(() => setFormError('Could not load level routines'));
  }, [linking, levels]);

  useEffect(() => {
    if (!(isNew && mode === 'set') || setRoutines) return;
    axios.get('/api/booking/competition-events/set-routines')
      .then(res => setSetRoutines(res.data))
      .catch(() => setFormError('Could not load competition set routines'));
  }, [isNew, mode, setRoutines]);

  // Fill the form from a competition set routine. It becomes an ordinary
  // routine for this gymnast, so it can be changed like any other.
  const chooseSetRoutine = (id) => {
    setSetRoutineId(id);
    const chosen = (setRoutines || []).find(r => r.id === id);
    if (!chosen) return;
    const byId = new Map(availableSkills.map(sk => [sk.id, sk]));
    setRows(chosen.skills.map(({ skillId, name: setName }) => {
      const skill = byId.get(skillId);
      return withKey({
        skillId, name: skill?.name || setName, figNotation: skill?.figNotation,
        difficulty: skill?.difficulty, dmtDifficulty: skill?.dmtDifficulty, isImplicit: false,
      });
    }));
    setApparatus(chosen.apparatus);
    fillName(chosen.name);
  };
  const chosenSet = (setRoutines || []).find(r => r.id === setRoutineId);

  // Fill the form from a suggested routine. For double mini the form takes
  // Pass 1 and Pass 2 is kept to save alongside it, as suggested.
  const [secondPass, setSecondPass] = useState(null);
  const [saveSecondPass, setSaveSecondPass] = useState(true);
  const suggestedRow = (s, a) => withKey({
    skillId: s.skillId, name: s.name, figNotation: s.figNotation,
    difficulty: a === 'DMT' ? null : s.difficulty, dmtDifficulty: a === 'DMT' ? s.difficulty : null, isImplicit: false,
  });
  const useSuggestion = ({ apparatus: a = 'TRA', name: suggestedName, skills, passes }) => {
    const [first, second] = passes || [{ name: suggestedName, skills }];
    setRows(first.skills.map(s => suggestedRow(s, a)));
    setApparatus(a);
    fillName(first.name);
    setSecondPass(second ? { name: second.name, rows: second.skills.map(s => suggestedRow(s, a)) } : null);
    setSaveSecondPass(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (linking && !levelRoutineId) return setFormError('Choose a level routine to link');
    if (!linking && !name.trim()) return setFormError('Give the routine a name');

    // Status and notes are only on the form when editing; a new routine
    // starts as Working on, with no notes
    const payload = { apparatus };
    if (!isNew) payload.notes = notes.trim() || null;
    if (!isNew && !byGymnast) payload.status = status;
    if (name.trim()) payload.name = name.trim();
    if (linking) payload.levelRoutineId = levelRoutineId;
    if (editsSkills) payload.skills = toPayloadSkills(rows);

    let display;
    if (linking) {
      const level = (levels || []).find(l => (l.routines || []).some(r => r.id === levelRoutineId));
      const levelRoutine = level?.routines.find(r => r.id === levelRoutineId);
      const skills = levelRoutine ? levelRoutineSkills(levelRoutine) : [];
      display = {
        name: payload.name || levelRoutine?.name || `Level ${level?.identifier} routine`,
        skills,
        levelRoutine: level ? { id: levelRoutineId, name: levelRoutine.name, level: { id: level.id, identifier: level.identifier, name: level.name } } : null,
        totalDifficulty: totalDifficulty(skills, apparatus),
      };
    } else if (editsSkills) {
      const skills = rows.map((r, i) => ({
        id: r.key, skillId: r.isImplicit ? null : r.skillId, name: r.name, figNotation: r.figNotation || null,
        difficulty: r.difficulty ?? null, dmtDifficulty: r.dmtDifficulty ?? null, isImplicit: Boolean(r.isImplicit), order: i + 1,
      }));
      display = { skills, totalDifficulty: totalDifficulty(skills, apparatus) };
    }

    setSaving(true);
    try {
      if (isNew && mode === 'suggest' && secondPass && saveSecondPass) {
        await onSave(payload, display);
        const second = { ...payload, name: secondPass.name, skills: toPayloadSkills(secondPass.rows) };
        const skills = secondPass.rows.map((r, i) => ({
          id: r.key, skillId: r.skillId, name: r.name, figNotation: r.figNotation || null,
          difficulty: null, dmtDifficulty: r.dmtDifficulty ?? null, isImplicit: false, order: i + 1,
        }));
        await onSave(second, { skills, totalDifficulty: totalDifficulty(skills, apparatus) });
      } else {
        await onSave(payload, display);
      }
    } catch (_err) {
      // The parent shows the error; keep the form as it is.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay gymnast-routine-editor__overlay">
      <div className="modal gymnast-routine-editor">
        <div className="modal-header">
          <h3>{isNew ? 'Add routine' : 'Edit routine'}</h3>
          <button type="button" onClick={onCancel} className="close-button">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {byGymnast && (
            <p className="text-muted">Your coach will check this routine before it can be used in a competition.</p>
          )}

          <section className="gymnast-routine-editor__section">
          <div className="form-group">
            <label htmlFor="routine-name">Name</label>
            <input
              id="routine-name"
              className="form-control"
              value={name}
              maxLength={100}
              onChange={(e) => { setName(e.target.value); setNameFilled(false); }}
              placeholder={linking ? "Defaults to the level routine's name" : mode === 'suggest' ? 'Leave blank to use the suggested name' : 'e.g. Comp voluntary, DMT Pass 1'}
            />
          </div>
          <div className="gymnast-routine-editor__row">
            <div className="form-group">
              <label htmlFor="routine-apparatus">Apparatus</label>
              <select id="routine-apparatus" className="form-control" value={apparatus} onChange={(e) => setApparatus(e.target.value)}>
                {APPARATUS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            {!isNew && !byGymnast && (
              <div className="form-group">
                <label htmlFor="routine-status">Status</label>
                <select id="routine-status" className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {Object.entries(STATUS).map(([value, s]) => <option key={value} value={value}>{s.label}</option>)}
                </select>
              </div>
            )}
          </div>
          </section>

          {showStart && (
          <section className="gymnast-routine-editor__section">
          <h4 className="gymnast-routine-editor__section-title">Start from</h4>
          {isNew && !byGymnast && (
              <div className="gymnast-routine-editor__mode" role="radiogroup" aria-label="Routine type">
                {MODES.filter(m => (m.id !== 'suggest' || (gymnastId && has('suggestRoutine'))) && (m.id !== 'set' || has('competitions'))).map(m => (
                  <label key={m.id} className={`gymnast-routine-editor__segment${mode === m.id ? ' is-selected' : ''}`}>
                    <input type="radio" name="mode" aria-label={m.label} checked={mode === m.id} onChange={() => setMode(m.id)} />
                    <span className="gymnast-routine-editor__segment-title">{m.title}</span>
                    <span className="gymnast-routine-editor__segment-hint">{m.hint}</span>
                  </label>
                ))}
              </div>
          )}

          {isNew && mode === 'suggest' && (
            <RoutineSuggester gymnastId={gymnastId} defaultApparatus={defaultApparatus} onSuggested={useSuggestion} />
          )}
          {isNew && mode === 'suggest' && secondPass && (
            <label className="routine-suggester__second-pass">
              <input type="checkbox" checked={saveSecondPass} onChange={(e) => setSaveSecondPass(e.target.checked)} />
              {' '}Also save {secondPass.name}: {secondPass.rows.map(r => r.name).join(', ')}
            </label>
          )}

          {isLinked && (
            <p className="text-muted">
              Skills come from Level {routine.levelRoutine.level.identifier} and stay in sync with it.
              Use Customise to change them for this gymnast.
            </p>
          )}

          {linking && (
            <div className="form-group">
              <label htmlFor="routine-level-link">Level routine</label>
              <select
                id="routine-level-link"
                className="form-control"
                value={levelRoutineId}
                onChange={(e) => setLevelRoutineId(e.target.value)}
              >
                <option value="">{levels ? 'Choose a routine…' : 'Loading…'}</option>
                {(levels || []).filter(l => l.routines?.length > 0).map(level => (
                  <optgroup key={level.id} label={`Level ${level.identifier}: ${level.name}`}>
                    {level.routines.map(r => (
                      <option key={r.id} value={r.id}>{levelRoutineLabel(r)}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          {isNew && mode === 'set' && (
            <div className="form-group">
              <label htmlFor="routine-set-copy">Competition set routine</label>
              <select
                id="routine-set-copy"
                className="form-control"
                value={setRoutineId}
                onChange={(e) => chooseSetRoutine(e.target.value)}
              >
                <option value="">
                  {!setRoutines ? 'Loading…' : setRoutines.length ? 'Choose a set routine…' : 'No competition categories have a set routine'}
                </option>
                {[...new Set((setRoutines || []).map(r => r.eventName))].map(eventName => (
                  <optgroup key={eventName} label={eventName}>
                    {setRoutines.filter(r => r.eventName === eventName).map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {chosenSet && (
                <small className="text-muted">
                  Used in {chosenSet.categories.join(', ')}. This makes a copy, so changes to the competition won't update it.
                </small>
              )}
            </div>
          )}

          </section>
          )}


          {editsSkills && (
            <section className="gymnast-routine-editor__section">
            <h4 className="gymnast-routine-editor__section-title">Skills</h4>
            <RoutineSkillsEditor
              rows={rows}
              onChange={setRows}
              apparatus={apparatus}
              availableSkills={availableSkills}
              gymnastId={gymnastId || routine?.gymnastId}
            />
            </section>
          )}

          {!isNew && (
          <section className="gymnast-routine-editor__section">
          <h4 className="gymnast-routine-editor__section-title">Notes</h4>
          <div className="form-group">
            <label htmlFor="routine-notes" className="visually-hidden">Notes</label>
            <textarea
              id="routine-notes"
              className="form-control"
              rows={3}
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <small className="text-muted">Visible to the gymnast and their family.</small>
          </div>
          </section>
          )}

          {formError && <p className="gymnast-routines__error">{formError}</p>}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : saveLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GymnastRoutineEditor;
